import { FormEvent } from 'react'
import WalletLoader from 'components/WalletLoader'
import { useSigningClient } from 'contexts/cosmwasm'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LineAlert from 'components/LineAlert'
import { InstantiateResult } from '@cosmjs/cosmwasm-stargate'
import { InstantiateMsg, Voter } from 'types/cw3'

const MULTISIG_CODE_ID =
  parseInt(import.meta.env.VITE_MULTISIG_CODE_ID as string) || 49

function AddressRow({ idx, readOnly }: { idx: number; readOnly: boolean }) {
  return (
    <tr key={idx}>
      <td className="pr-2 pb-2">
        <input
          className="block box-border m-0 w-full rounded-lg px-4 py-2 bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono transition-all"
          type="text"
          name={`address_${idx}`}
          placeholder="wallet address..."
          size={45}
          readOnly={readOnly}
        />
      </td>
      <td className="pb-2">
        <input
          type="number"
          className="block box-border m-0 w-full rounded-lg px-4 py-2 bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono transition-all"
          name={`weight_${idx}`}
          defaultValue="1"
          min={1}
          max={999}
          readOnly={readOnly}
        />
      </td>
    </tr>
  )
}

function validateNonEmpty(msg: InstantiateMsg, label: string) {
  const { required_weight, max_voting_period, voters } = msg
  if (isNaN(required_weight) || isNaN(max_voting_period.time)) {
    return false
  }
  if (label.length === 0) {
    return false
  }
  if (
    voters.some(({ addr, weight }: Voter) => addr.length === 0 || isNaN(weight))
  ) {
    return false
  }
  return true
}

interface FormElements extends HTMLFormControlsCollection {
  duration: HTMLInputElement
  threshold: HTMLInputElement
  label: HTMLInputElement
  [key: string]: any
}

interface MultisigFormElement extends HTMLFormElement {
  readonly elements: FormElements
}

const CreateMultisig = () => {
  const navigate = useNavigate()
  const { walletAddress, signingClient } = useSigningClient()
  const [count, setCount] = useState(2)
  const [contractAddress, setContractAddress] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = (event: FormEvent<MultisigFormElement>) => {
    event.preventDefault()
    setError('')
    setLoading(true)

    const formEl = event.currentTarget as MultisigFormElement

    const voters = [...Array(count)].map((_item, index) => ({
      addr: formEl[`address_${index}`]?.value?.trim(),
      weight: parseInt(formEl[`weight_${index}`]?.value?.trim()),
    }))
    const required_weight = parseInt(formEl.threshold.value?.trim())
    const max_voting_period = {
      time: parseInt(formEl.duration.value?.trim()),
    }

    const msg = {
      voters,
      required_weight,
      max_voting_period,
    }

    const label = formEl.label.value.trim()

    // @ebaker TODO: add more validation
    if (!validateNonEmpty(msg, label)) {
      setLoading(false)
      setError('All fields are required.')
      return
    }

    if (!signingClient) {
      setLoading(false)
      setError('Please try reconnecting your wallet.')
      return
    }

    signingClient
      .instantiate(walletAddress, MULTISIG_CODE_ID, msg, label, 'auto')
      .then((response: InstantiateResult) => {
        setLoading(false)
        if (response.contractAddress.length > 0) {
          setContractAddress(response.contractAddress)
        }
      })
      .catch((err: any) => {
        setLoading(false)
        console.log('err', err)
        setError(err.message)
      })
  }

  const complete = contractAddress.length > 0

  return (
    <WalletLoader>
      <div className="text-center container mx-auto max-w-lg">
        <h1 className="text-5xl font-bold mb-8">New Multisig</h1>
        <form
          className="container mx-auto max-w-lg mb-8"
          onSubmit={handleSubmit}
        >
          <table className="w-full mb-8">
            <thead>
              <tr>
                <th>Address</th>
                <th>Weight</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(count)].map((_item, index) => (
                <AddressRow key={index} idx={index} readOnly={complete} />
              ))}
              <tr>
                <td colSpan={2} className="text-right">
                  <button
                    className="px-6 py-2 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full transition-colors font-medium"
                    onClick={(e) => {
                      e.preventDefault()
                      setCount(count + 1)
                    }}
                  >
                    + Add another
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <table className="w-full my-4">
            <thead>
              <tr>
                <th className="text-left pb-2 text-foreground">Threshold</th>
                <th className="text-left box-border px-2 text-sm pb-2 text-foreground">
                  Max Voting Period (seconds)
                </th>
                <th className="text-left pb-2 text-foreground">Label</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    className="block box-border m-0 w-full rounded-lg px-4 py-2 bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    name="threshold"
                    type="number"
                    defaultValue={count}
                    min={1}
                    max={999}
                    readOnly={complete}
                  />
                </td>
                <td className="box-border px-2">
                  <input
                    className="block box-border m-0 w-full rounded-lg px-4 py-2 bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    name="duration"
                    type="number"
                    placeholder="duration in seconds"
                    min={1}
                    max={2147483647}
                    defaultValue={604800}
                    readOnly={complete}
                  />
                </td>
                <td>
                  <input
                    className="block box-border m-0 w-full rounded-lg px-4 py-2 bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    name="label"
                    type="text"
                    placeholder="My multisig name"
                    readOnly={complete}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          {!complete && (
            <button
              className={`px-8 py-4 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 text-2xl rounded-full w-full shadow-lg transition-all ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
              type="submit"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Multisig'}
            </button>
          )}
        </form>

        {error && <LineAlert variant="error" msg={error} />}

        {contractAddress !== '' && (
          <div className="text-right">
            <LineAlert variant="success" msg={`Success!`} />
            <button
              className="mt-4 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold"
              onClick={(e) => {
                e.preventDefault()
                navigate(`/${encodeURIComponent(contractAddress)}`)
              }}
            >
              View Multisig &#8599;
            </button>
          </div>
        )}
      </div>
    </WalletLoader>
  )
}

export default CreateMultisig
